import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscriptions } from '../../hooks/useSubscriptions';
import { getStoredUser } from '../../utils/authStorage';
import examIcon from '../../assets/images/b1.png';
import { SUBSCRIPTION_ROUTES } from './subscriptionRoutes';
import '../DashboardSkeleton.css';
import './SubscriptionCard.css';

const cardStyles = {
  'exam-subscriptions': { iconClass: 'exam-icon', color: '#009b91' },
  'subject-subscriptions': { iconClass: 'subject-icon', color: '#4d63bf' },
  'assessment-subscriptions': { iconClass: 'assessment-icon', color: '#ff6377' },
  'purchase-gamification-coins': { iconClass: 'coins-icon', color: '#d69b13', titleWords: 1 },
  'purchase-assessment-reattempts': { iconClass: 'reattempt-icon', color: '#f16a00' },
  'purchase-rapid-fire-attempts': { iconClass: 'rapid-icon', color: '#ef222d' },
  'single-item-purchase': { iconClass: 'purchase-icon', color: '#8d8d8d' },
};

function splitName(name, titleWords) {
  const words = String(name || '').trim().split(/\s+/);
  const splitAt = titleWords || Math.max(1, words.length - 1);
  return [words.slice(0, splitAt).join(' '), words.slice(splitAt).join(' ')];
}

const SubscriptionCard = ({ item, isAddon = false, onSelect }) => {
  const { iconClass = '', color = '#4d63bf', titleWords } = cardStyles[item.slug] || {};
  const [title, highlight] = splitName(item.name, titleWords);
  const unavailable = item.slug === 'single-item-purchase';

  return (
    <div className={`subscription-card ${isAddon ? 'addon-card' : ''} ${unavailable ? 'subscription-card--unavailable' : ''}`}>
      <div className="subscription-flex">
        <div className={`subscription-iconn ${iconClass}`}>
          <img src={item.icon || examIcon} alt="" onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = examIcon; }} />
        </div>
        <div className="subscription-content">
          <h3>
            {title}
            <span style={{ color }}>{highlight}</span>
          </h3>
        </div>
      </div>

      {!isAddon && <p className="subscription-description">{item.description}</p>}

      <button type="button" className="subscription-action" style={{ color }} disabled={unavailable} onClick={() => onSelect(item)}>
        <span>{isAddon ? 'View plan' : 'Explore now'}</span>
        <i className={isAddon ? 'bi bi-arrow-up-right' : 'bi bi-arrow-right-short'}></i>
      </button>
    </div>
  );
};

const SubscriptionCardShimmer = ({ isAddon = false }) => (
  <div className={`subscription-card subscription-card--shimmer ${isAddon ? 'addon-card' : ''}`} aria-hidden="true" data-testid="subscription-card-shimmer">
    <div className="subscription-flex">
      <span className="subscription-iconn dashboard-skeleton" />
      <div className="subscription-content">
        <span className="dashboard-skeleton dashboard-skeleton--line subscription-shimmer-title" />
        <span className="dashboard-skeleton dashboard-skeleton--line subscription-shimmer-title subscription-shimmer-title--short" />
      </div>
    </div>
    {!isAddon && (
      <div className="subscription-shimmer-description">
        <span className="dashboard-skeleton dashboard-skeleton--line" />
        <span className="dashboard-skeleton dashboard-skeleton--line" />
        <span className="dashboard-skeleton dashboard-skeleton--line subscription-shimmer-title--short" />
      </div>
    )}
    <div className="subscription-action">
      <span className="dashboard-skeleton dashboard-skeleton--line subscription-shimmer-action" />
      <span className="dashboard-skeleton dashboard-skeleton--icon subscription-shimmer-arrow" />
    </div>
  </div>
);

const SubscriptionListShimmer = () => (
  <section className="subscription-wrapper" role="status" aria-label="Loading subscriptions">
    <div className="subscription-grid">
      {Array.from({ length: 3 }, (_, index) => <SubscriptionCardShimmer key={index} />)}
    </div>
    <div className="addons-heading"><span></span><h2>Add ons</h2></div>
    <div className="subscription-grid">
      {Array.from({ length: 4 }, (_, index) => <SubscriptionCardShimmer key={index} isAddon />)}
    </div>
  </section>
);

const SubscriptionCards = () => {
  const navigate = useNavigate();
  const storedUser = getStoredUser();
  const lang = String(storedUser?.content_language || storedUser?.language || 'EN').toUpperCase().startsWith('HI') ? 'HI' : 'EN';
  const { data, isLoading, isFetching, isError, error, refetch } = useSubscriptions(lang);
  const primarySubscriptions = data?.primarySubscriptions || [];
  const addonSubscriptions = data?.addonSubscriptions || [];

  const selectSubscription = (item) => {
    if (item.slug === 'single-item-purchase') return;
    const destinations = {
      'purchase-gamification-coins': SUBSCRIPTION_ROUTES.coinStore,
      'purchase-assessment-reattempts': SUBSCRIPTION_ROUTES.assessmentReattempts,
      'purchase-rapid-fire-attempts': SUBSCRIPTION_ROUTES.rapidFireAttempts,
      'subject-subscriptions': SUBSCRIPTION_ROUTES.subjectPlans,
      'assessment-subscriptions': SUBSCRIPTION_ROUTES.assessmentPlans,
      'exam-subscriptions': SUBSCRIPTION_ROUTES.examPlans,
    };
    const destination = destinations[item.slug] || SUBSCRIPTION_ROUTES.paymentMethod;
    navigate(destination, {
      state: { subscription: item, subscriptionSlug: item.slug },
    });
  };

  if (isLoading || isFetching) {
    return <SubscriptionListShimmer />;
  }

  if (isError) {
    return <section className="subscription-wrapper" role="alert">{error?.message || 'Unable to load subscriptions.'} <button type="button" onClick={() => refetch()}>Retry</button></section>;
  }

  if (!primarySubscriptions.length && !addonSubscriptions.length) {
    return <section className="subscription-wrapper">No subscriptions available.</section>;
  }

  return (
    <section className="subscription-wrapper">
      <div className="subscription-grid">
        {primarySubscriptions.map((item) => (
          <SubscriptionCard key={item.slug} item={item} onSelect={selectSubscription} />
        ))}
      </div>

      {addonSubscriptions.length > 0 && (
        <>
          <div className="addons-heading">
            <span></span>
            <h2>Add ons</h2>
          </div>
          <div className="subscription-grid">
            {addonSubscriptions.map((item) => (
              <SubscriptionCard key={item.slug} item={item} isAddon onSelect={selectSubscription} />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default SubscriptionCards;
